import { Form, Head, Link, usePage } from "@inertiajs/react"
import { Calendar, Clock, Plus, Trophy, Users } from "lucide-react"
import { useState } from "react"

import { Divider } from "@/components/divider"
import { EmptyState } from "@/components/empty-state"
import { InputError } from "@/components/input-error"
import { PageHeader } from "@/components/page-header"
import { RaceTag } from "@/components/race-tag"
import { Rivets } from "@/components/rivets"
import { StandingBadge } from "@/components/standing-badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Spinner } from "@/components/ui/spinner"
import AppLayout from "@/layouts/app-layout"
import { leaguePath, leaguesPath } from "@/routes"
import type { DashboardLeague, SharedProps } from "@/types"

interface DashboardProps {
  leagues: DashboardLeague[]
  errors?: Record<string, string[]>
}

function seasonLabel(league: DashboardLeague): string {
  if (!league.season) return "No season yet"
  const { number, status } = league.season
  if (status === "draft") return `Season ${number} · Pre-Season Draft`
  if (status === "active" && league.current_round) {
    return `Season ${number} · Round ${league.current_round}`
  }
  if (status === "active") return `Season ${number} · Active`
  return `Season ${number} · Completed`
}

function formatMatchDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

interface CreateLeagueDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  errors?: Record<string, string[]>
}

function CreateLeagueDialog({
  open,
  onOpenChange,
  errors,
}: CreateLeagueDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="relative sm:max-w-md" showCloseButton={false}>
        <Rivets />
        <DialogHeader>
          <DialogTitle
            className="font-display text-3xl leading-none"
            style={{
              color: "var(--accent)",
              textShadow:
                "0 0 6px oklch(0.8 0.16 85 / 0.3), 0 0 16px oklch(0.7 0.14 85 / 0.12), 0 0 24px oklch(0.8 0.3 145 / 0.2), 0 1px 0 oklch(0.55 0.1 85), 0 2px 4px oklch(0 0 0 / 0.5)",
            }}
          >
            Found a League
          </DialogTitle>
          <DialogDescription>
            Name your league and prepare the pitch for battle.
          </DialogDescription>
        </DialogHeader>
        <Form
          method="post"
          action={leaguesPath()}
          className="grid gap-4"
          onSuccess={() => onOpenChange(false)}
        >
          {({ processing }) => (
            <>
              <div className="grid gap-2">
                <Label
                  htmlFor="league-name"
                  className="text-accent-dim text-xs font-bold tracking-wider uppercase"
                >
                  League Name
                </Label>
                <Input
                  id="league-name"
                  name="league[name]"
                  placeholder="e.g. Friday Night Blood Bowl"
                  autoFocus
                  autoComplete="off"
                  aria-invalid={!!errors?.name || undefined}
                  aria-describedby={
                    errors?.name ? "league-name-error" : undefined
                  }
                />
                <InputError id="league-name-error" messages={errors?.name} />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => onOpenChange(false)}
                  disabled={processing}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={processing}>
                  {processing && <Spinner />}
                  Found League
                </Button>
              </DialogFooter>
            </>
          )}
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export default function Dashboard({
  leagues,
  errors: pageErrors,
}: DashboardProps) {
  const { auth } = usePage<SharedProps>().props
  const isAdmin = auth.user.admin
  const [dialogOpen, setDialogOpen] = useState(() =>
    Boolean(pageErrors && Object.keys(pageErrors).length > 0),
  )

  return (
    <AppLayout>
      <Head title="Dashboard" />

      <div className="mx-auto w-full max-w-7xl px-4 py-8">
        <PageHeader
          title="Your Leagues"
          description="Blood, glory, and the favour of Nuffle await"
          actions={
            isAdmin ? (
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="mr-2 size-4" />
                Found a League
              </Button>
            ) : null
          }
        />

        {leagues.length > 0 ? (
          <div className="grid gap-10 sm:grid-cols-2 lg:grid-cols-3">
            {leagues.map((league) => {
              return (
                <Link
                  key={league.id}
                  href={leaguePath(league.id)}
                  className="group block"
                >
                  <Card className="cursor-pointer overflow-visible transition-shadow hover:shadow-[0_4px_16px_oklch(0_0_0/0.6)]">
                    <CardContent className="flex flex-col gap-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0 flex-1">
                          <h3
                            className="font-display truncate text-2xl leading-tight font-bold transition-colors group-hover:text-[var(--accent)]"
                            style={{
                              color: "var(--accent)",
                              textShadow:
                                "0 0 4px oklch(0.8 0.16 85 / 0.25), 0 0 16px oklch(0.8 0.3 145 / 0.12), 0 1px 0 oklch(0.55 0.1 85), 0 1px 3px oklch(0 0 0 / 0.4)",
                            }}
                          >
                            {league.name}
                          </h3>
                          <p className="text-muted-foreground mt-0.5 text-sm">
                            {seasonLabel(league)}
                          </p>
                        </div>
                        <StandingBadge position={league.standing_position} />
                      </div>

                      <div className="flex flex-wrap items-center gap-4 text-xs font-semibold tracking-wide text-[var(--accent-dim)] uppercase">
                        <span className="flex items-center gap-1.5">
                          <Users className="h-3.5 w-3.5" />
                          {league.coach_count}{" "}
                          {league.coach_count === 1 ? "coach" : "coaches"}
                        </span>
                        {league.schedule_status === "match_day" &&
                          league.next_match_date && (
                            <span className="flex items-center gap-1.5">
                              <Calendar className="h-3.5 w-3.5" />
                              {`Match day: ${formatMatchDate(league.next_match_date)}`}
                            </span>
                          )}
                        {league.schedule_status === "awaiting_coaches" && (
                          <span className="flex items-center gap-1.5">
                            <Clock className="h-3.5 w-3.5" />
                            Awaiting coaches
                          </span>
                        )}
                      </div>

                      <Divider />

                      <div>
                        {league.user_team ? (
                          <div className="flex items-center justify-between text-sm">
                            <div>
                              <p className="text-xs font-semibold tracking-wide text-[var(--accent-dim)] uppercase">
                                Your team
                              </p>
                              <p className="mt-0.5 font-semibold">
                                {league.user_team.name}
                              </p>
                            </div>
                            <RaceTag
                              race={league.user_team.race}
                              hue={league.user_team.color_hue ?? undefined}
                            />
                          </div>
                        ) : (
                          <div className="flex items-center justify-between text-sm">
                            <p className="text-xs font-semibold tracking-wide text-[var(--accent-dim)] uppercase">
                              Your team
                            </p>
                            <span
                              style={{
                                background: "oklch(0.25 0.08 28)",
                                color: "oklch(0.70 0.18 28)",
                                border: "1px solid oklch(0.38 0.12 28)",
                              }}
                              className="px-2 py-0.5 text-xs font-bold tracking-wide uppercase"
                            >
                              Draft Now
                            </span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              )
            })}
          </div>
        ) : (
          <EmptyState
            icon={Trophy}
            shieldIcon
            title="The Pitch Awaits"
            description={
              isAdmin
                ? "No leagues yet. Found one and rally your friends, or ask a fellow coach for an invite link to join the fray."
                : "No leagues yet. Ask a fellow coach for an invite link to join the fray."
            }
            action={
              isAdmin ? (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="mr-2 size-4" />
                  Found a League
                </Button>
              ) : undefined
            }
          />
        )}
      </div>

      {isAdmin && (
        <CreateLeagueDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          errors={pageErrors}
        />
      )}
    </AppLayout>
  )
}
